import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import type { LaborStatusPanelProps } from "./LaborStatusPanel";

type LaborDevStatusPanelContextValue = {
  panelProps: LaborStatusPanelProps | null;
  setLaborDevStatusPanel: (next: LaborStatusPanelProps | null) => void;
};

const LaborDevStatusPanelContext = createContext<LaborDevStatusPanelContextValue | null>(null);

export function LaborDevStatusProvider({ children }: { children: ReactNode }): JSX.Element {
  const [panelProps, setPanelProps] = useState<LaborStatusPanelProps | null>(null);
  const setLaborDevStatusPanel = useCallback((next: LaborStatusPanelProps | null) => {
    setPanelProps(next);
  }, []);
  const value = useMemo(
    () => ({ panelProps, setLaborDevStatusPanel }),
    [panelProps, setLaborDevStatusPanel],
  );
  return <LaborDevStatusPanelContext.Provider value={value}>{children}</LaborDevStatusPanelContext.Provider>;
}

export function useLaborDevStatusPanelSnapshot(): LaborStatusPanelProps | null {
  return useContext(LaborDevStatusPanelContext)?.panelProps ?? null;
}

/**
 * Publishes {@link LaborStatusPanel} props into the account menu while this labor surface is mounted.
 * Memoize `snapshot` in the caller to avoid unnecessary effect runs.
 */
export function useLaborDevStatusPanelRegistration(active: boolean, snapshot: LaborStatusPanelProps): void {
  const setLaborDevStatusPanel = useContext(LaborDevStatusPanelContext)?.setLaborDevStatusPanel;
  useEffect(() => {
    if (!import.meta.env.DEV || setLaborDevStatusPanel == null) {
      return;
    }
    if (!active) {
      setLaborDevStatusPanel(null);
      return () => {
        setLaborDevStatusPanel(null);
      };
    }
    setLaborDevStatusPanel(snapshot);
    return () => {
      setLaborDevStatusPanel(null);
    };
  }, [active, setLaborDevStatusPanel, snapshot]);
}
